// Item summary for the quote item window (Stephanie 2026-10-14, idea F, Jake
// approved): margin % / $, subtotal, tax and total for ONE line, side by side
// with the fields that change them.
//
// No new money math. Every figure comes from the shared module the server bills
// with (packages/shared/src/order-money.ts), the same calls DocumentLineCard
// makes, so the summary and the line's Total row can never disagree:
//   • subtotal = lineTotalCents (goods + decoration charges)
//   • cost     = lineAllInCostCents (goods cost + charge cost)
//   • margin % = lineMarginPctWithCharges (both sides include the charges)
//   • tax      = the line card's own expression; no rate yet → "—", never $0
// Rep-only: it renders cost and margin, so it lives only in the staff editor.

import {
  formatCents,
  formatPct,
  lineAllInCostCents,
  lineChargesCents,
  lineGoodsCents,
  lineTotalCents,
} from "./money";
import { divideRoundHalfUp, lineMarginPctWithCharges, rateHundredths } from "@merchd/shared";
import type { DocLine } from "./types";
import { cn } from "@/lib/utils";

export function LineItemSummary(props: {
  line: DocLine;
  currency: string;
  /** The quote's tax rate (orders.default_tax_rate). null/absent = not set. */
  taxRatePct?: string | null;
}) {
  const { line, currency, taxRatePct } = props;
  const moneyLine = {
    unitPrice: line.unitPrice,
    quantityTotal: line.quantityTotal,
    variants: line.variants.map((v) => ({
      quantity: v.quantity,
      unitPrice: v.unitPrice,
      unitCost: v.unitCost,
    })),
    decorationCharges: (line.decorations ?? []).flatMap((d) =>
      d.charges.map((c) => ({ amount: c.amount, cost: c.cost, billingModel: c.billingModel, quantity: c.quantity ?? null })),
    ),
    netCost: line.netCost ?? "0",
  };

  const goodsCents = lineGoodsCents(moneyLine);
  const chargesCents = lineChargesCents(moneyLine);
  const subtotalCents = lineTotalCents(moneyLine);
  const costCents = lineAllInCostCents(moneyLine);
  const marginPct = lineMarginPctWithCharges(moneyLine);
  // Margin $ only means something once there's a cost to take away.
  const marginCents = costCents > 0 ? subtotalCents - costCents : null;

  const rateH = taxRatePct != null && taxRatePct !== "" ? rateHundredths(taxRatePct) : null;
  const taxCents =
    line.serviceType === "shipping"
      ? 0
      : rateH == null
        ? null
        : divideRoundHalfUp(subtotalCents * rateH, 10_000);
  const totalCents = subtotalCents + (taxCents ?? 0);

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="self-start rounded-lg bg-cream-100/70 p-4">
        <dl className="space-y-2 text-[15px] tabular-nums text-brown-500">
          <Row label="Margin" value={formatPct(marginPct)} strong tone={marginPct != null && marginPct < 0 ? "bad" : undefined} />
          <Row
            label="Margin amount"
            value={marginCents == null ? "—" : formatCents(marginCents, currency)}
            strong
            tone={marginCents != null && marginCents < 0 ? "bad" : undefined}
          />
          <Row label="Cost" value={costCents > 0 ? formatCents(costCents, currency) : "—"} muted />
        </dl>
        <p className="mt-2 text-xs text-brown-300">
          Includes decoration charges. Only staff see cost and margin.
        </p>
      </div>
      <div className="rounded-lg bg-cream-100/70 p-4">
        <dl className="space-y-2 text-[15px] tabular-nums text-brown-500">
          <Row label="Products" value={formatCents(goodsCents, currency)} muted />
          <Row label="Decoration & costs" value={formatCents(chargesCents, currency)} muted />
          <Row label="Subtotal" value={formatCents(subtotalCents, currency)} strong />
          <Row
            label={rateH == null ? "Tax (no rate set)" : `Tax (${Number(taxRatePct)}%)`}
            value={taxCents == null ? "—" : formatCents(taxCents, currency)}
          />
          <Row label="Total" value={formatCents(totalCents, currency)} strong big />
        </dl>
      </div>
    </div>
  );
}

function Row(props: {
  label: string;
  value: string;
  strong?: boolean;
  big?: boolean;
  muted?: boolean;
  tone?: "bad";
}) {
  const { label, value, strong, big, muted, tone } = props;
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-3",
        strong && "font-semibold",
        big && "border-t border-brown-200 pt-2 text-lg font-bold",
      )}
    >
      <dt className={cn(muted ? "text-brown-300" : "text-brown-400", strong && "text-brown-500")}>
        {label}
      </dt>
      <dd className={cn(muted && "text-brown-300", tone === "bad" && "text-rust-600")}>{value}</dd>
    </div>
  );
}
